import {
	bufferTime,
	catchError,
	distinctUntilChanged,
	EMPTY,
	filter,
	fromEvent,
	map,
	merge,
	Observable,
	of,
	ReplaySubject,
	shareReplay,
	skip,
	startWith,
	Subject,
	Subscription,
	tap,
	timeout,
	zip,
} from 'rxjs'
import { SchmancyTeleportation } from '@schmancy/router/teleport'
import { ActiveRoute, AreaDeparture, AreaSubscription, RouteAction } from './router.types'
import { buildPathFromPattern, isDynamicRoutePattern, patternParamNames } from './utils'

export const routerHistory = new Subject<RouteAction>()

export const FINDING_MORTIES = 'FINDING_MORTIES'
export const HERE_RICKY = 'HERE_RICKY'
export type HERE_RICKY_EVENT = CustomEvent<{
	component: SchmancyTeleportation
}>
export type FINDING_MORTIES_EVENT = CustomEvent<{
	component: SchmancyTeleportation
}>

// WeakMap keyed on the singleton instance so its per-area subjects are
// collectible if the service itself is ever dropped.
const areaSubjectsCache = new WeakMap<AreaService, Map<string, ReplaySubject<ActiveRoute>>>()

type NavigationSource = 'programmatic' | 'browser' | 'initial'

class AreaService implements AreaSubscription {
	private static instance: AreaService
	public prettyURL = false
	// The one area `createCleanURL` treats as address-owning: a dynamic-pattern
	// route on this area builds its concrete path straight from the pattern (no
	// JSON-blob fallback), and `prettyURL` derives its path from this area's
	// component name. Defaults to 'main' — every existing consumer whose primary
	// area is literally named "main" keeps today's behaviour unchanged. A host
	// app whose primary `<schmancy-area>` carries a different name (e.g. "root")
	// sets `area.addressArea = 'root'` once, at startup, to opt that area into
	// clean dynamic-pattern URLs instead.
	//
	// WHILE THIS AREA SHOWS A DYNAMIC-PATTERN ROUTE, THE ADDRESS IS THAT PATTERN'S
	// PATH AND NOTHING ELSE — every other area's route is carried in
	// `history.state` (so back/forward and reload restore it) but is absent from
	// the URL, so a link typed, pasted, or shared from that address cold-loads the
	// other areas from their own `when`/`.default` matching. `prettyURL` makes the
	// same trade for the same reason; see `RouteConfig.when` for the consumer-side
	// statement of it.
	public addressArea = 'main'
	public mode: 'SILENT' | 'HISTORY' = 'HISTORY'
	// Future-only live stream — a mounting area's startup arm reads pendingByArea instead.
	public request = new Subject<RouteAction>()
	// Last real push per area; pop() deliberately does NOT clear this so a stale disconnect-pop
	// cannot clobber a fresh navigation written before remount. consumePending() claims+deletes at
	// mount; a pushed-but-never-mounted area keeps one entry (bounded by area-name count — the held
	// `component` is a tag string or lazy fn, so no DOM node is retained).
	public pendingByArea = new Map<string, RouteAction>()
	public current = new Map<string, ActiveRoute>()
	public $current = new ReplaySubject<Map<string, ActiveRoute>>(1)
	private urlSubject = new ReplaySubject<string>(1)
	private committedAddress = ''
	// The address as this service last wrote it FROM the address area's dynamic
	// pattern — null when the address is anything else. Derived, never declared:
	// it is `buildPathFromPattern` re-run over the route that ended up displayed.
	// It is captured at commit rather than computed where it is read because the
	// navigation that asks has already overwritten that route in `current`, and it
	// is re-checked against `location.pathname` at every read, so a stale value
	// answers "not mine" instead of being believed.
	private patternAuthoredAddress: string | null = null
	private popstateDocumentMatches = new WeakMap<PopStateEvent, boolean>()
	public readonly url$ = this.urlSubject.asObservable().pipe(
		distinctUntilChanged(),
		map(href => new URL(href)),
	)
	// The departure signal — area.component.ts .next()s here synchronously, before any
	// lazy import, the instant an incoming push's target differs from the area's active
	// route. departures$(areaName) is the scoped read side; a route-scoped reconciler
	// composes it into its own lifetime: takeUntil(merge(this.disconnecting, area.departures$(areaName))).
	public departureSubject = new Subject<AreaDeparture>()

	private get areaSubjects(): Map<string, ReplaySubject<ActiveRoute>> {
		let subjects = areaSubjectsCache.get(this)
		if (!subjects) {
			subjects = new Map()
			areaSubjectsCache.set(this, subjects)
		}
		return subjects
	}

	public enableHistoryMode = true
	private findingMortiesEvent = new CustomEvent<FINDING_MORTIES_EVENT['detail']>(FINDING_MORTIES)
	private disposed = false
	public isProcessingPopstate = false
	private browserSubscription?: Subscription

	public get url(): URL {
		return new URL(location.href)
	}

	constructor() {
		this._commitAddress()
		this._publishURL()
		this.$current.next(this.current)

		this.$current.subscribe(currentAreas => {
			if (this.disposed) return

			currentAreas.forEach((route, areaName) => {
				const areaSubject = this.getOrCreateAreaSubject(areaName)
				areaSubject.next(route)
			})
		})

		this.initializeFromBrowserState()

		if (typeof window !== 'undefined') {
			// 'unload' is deprecated and Chrome now blocks it via Permissions-Policy by
			// default (console violation, listener never fires) — 'pagehide' is the
			// standards-recommended replacement. Unlike 'unload', 'pagehide' also fires
			// when the page enters the bfcache (event.persisted === true); dispose()
			// permanently completes this singleton's Subjects, so disposing on a
			// bfcache-entry would leave a restored page's router dead — skip it there.
			this.browserSubscription = merge(
				fromEvent<PageTransitionEvent>(window, 'pagehide').pipe(
					filter(event => !event.persisted),
					tap(() => this.dispose()),
				),
				// A browser traversal supersedes queued programmatic intent before
				// mounted areas restore it or remounted children claim a startup route.
				fromEvent(window, 'popstate').pipe(tap(() => this.pendingByArea.clear())),
			).subscribe()
		}
	}

	private initializeFromBrowserState() {
		// No browser history outside a DOM (SSR / module-federation Node entry load) — nothing to restore.
		if (typeof history === 'undefined') return
		try {
			const browserState = history.state
			const restored = this.restoreFromBrowserState(browserState)
			if (Object.keys(restored).length === 0) return

			const schmancyAreas: Record<string, ActiveRoute> = {}
			Object.entries(restored).forEach(([areaName, route]) => {
				const activeRoute: ActiveRoute = { ...route, area: areaName }
				schmancyAreas[areaName] = activeRoute
				this.current.set(areaName, activeRoute)
			})
			this.$current.next(this.current)

			// updateBrowserHistory rebuilds the whole url out of history.state, which a fresh
			// navigation — a pasted, bookmarked, or shared deep link — leaves null, so unless
			// the segment goes back there before any area mounts, the first area to commit
			// writes a url naming only itself and erases the siblings that have not read theirs.
			if (!browserState?.schmancyAreas) history.replaceState({ ...browserState, schmancyAreas }, '')
		} catch (error) {
			console.warn('Failed to initialize from browser state:', error)
		}
	}

	private getOrCreateAreaSubject(areaName: string): ReplaySubject<ActiveRoute> {
		let subject = this.areaSubjects.get(areaName)

		if (!subject || subject.closed) {
			subject = new ReplaySubject<ActiveRoute>(1)
			this.areaSubjects.set(areaName, subject)

			const currentRoute = this.current.get(areaName)
			if (currentRoute) {
				subject.next({
					...currentRoute,
					state: currentRoute.state || {},
					params: currentRoute.params || {},
					props: currentRoute.props || {},
				})
			}
		}

		return subject
	}

	on(areaName: string, skipCurrent = false): Observable<ActiveRoute> {
		if (!areaName) {
			throw new Error('Area name is required')
		}

		const areaSubject = this.getOrCreateAreaSubject(areaName)
		const observable = areaSubject.asObservable().pipe(
			distinctUntilChanged(
				(a, b) =>
					a.component === b.component &&
					JSON.stringify(a.state) === JSON.stringify(b.state) &&
					JSON.stringify(a.params) === JSON.stringify(b.params) &&
					JSON.stringify(a.props) === JSON.stringify(b.props),
			),
			shareReplay(1),
		)

		return skipCurrent ? observable.pipe(skip(1)) : observable
	}

	all(skipCurrent = false): Observable<Map<string, ActiveRoute>> {
		const observable = this.$current.asObservable().pipe(shareReplay(1))
		return skipCurrent ? observable.pipe(skip(1)) : observable
	}

	/** One area's departure signal — fires `{ area, from, to }` whenever a push into
	 * `areaName` targets a different route than the one currently mounted there; a
	 * same-target (param-only) push never emits. */
	departures$(areaName: string): Observable<AreaDeparture> {
		if (!areaName) {
			throw new Error('Area name is required')
		}

		return this.departureSubject.pipe(filter(d => d.area === areaName))
	}

	getState<T = unknown>(areaName: string): Observable<T> {
		if (!areaName) {
			throw new Error('Area name is required')
		}

		return this.on(areaName).pipe(
			map(route => route.state),
			filter((state): state is NonNullable<Record<string, unknown>> => state !== undefined && state !== null),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
			map(state => state as T),
			catchError(err => {
				console.error(`Error getting state for area "${areaName}":`, err)
				return EMPTY
			}),
		)
	}

	// URL query params are merged with route params — URL takes precedence, to
	// carry an external OAuth-style redirect's query string through.
	params<T extends Record<string, unknown> = Record<string, unknown>>(areaName: string): Observable<T> {
		if (!areaName) {
			throw new Error('Area name is required')
		}

		const routeParams = (this.current.get(areaName)?.params ?? {}) as Record<string, unknown>

		let currentParams = { ...routeParams }
		if (typeof window !== 'undefined' && window.location.search) {
			const urlParams = new URLSearchParams(window.location.search)
			urlParams.forEach((value, key) => {
				currentParams[key] = value
			})
		}

		return this.on(areaName).pipe(
			map(route => (route.params ?? {}) as T),
			startWith(currentParams as T),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
			catchError(err => {
				console.error(`Error getting params for area "${areaName}":`, err)
				return EMPTY
			}),
		)
	}

	param<T = unknown>(areaName: string, key: string): Observable<T> {
		if (!areaName || !key) {
			throw new Error('Area name and key are required')
		}

		return this.params<Record<string, unknown>>(areaName).pipe(
			map(params => params[key]),
			filter((value): value is NonNullable<unknown> => value !== undefined),
			distinctUntilChanged(),
			map(value => value as T),
			catchError(err => {
				console.error(`Error getting param "${key}" for area "${areaName}":`, err)
				return EMPTY
			}),
		)
	}

	props<T extends Record<string, unknown> = Record<string, unknown>>(areaName: string): Observable<T> {
		if (!areaName) {
			throw new Error('Area name is required')
		}

		return this.on(areaName).pipe(
			map(route => route.props),
			filter((props): props is NonNullable<Record<string, unknown>> => props !== undefined && props !== null),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
			map(props => props as T),
			catchError(err => {
				console.error(`Error getting props for area "${areaName}":`, err)
				return EMPTY
			}),
		)
	}

	prop<T = unknown>(areaName: string, key: string): Observable<T> {
		if (!areaName || !key) {
			throw new Error('Area name and key are required')
		}

		return this.props<Record<string, unknown>>(areaName).pipe(
			map(props => props[key]),
			filter((value): value is NonNullable<unknown> => value !== undefined),
			distinctUntilChanged(),
			map(value => value as T),
			catchError(err => {
				console.error(`Error getting prop "${key}" for area "${areaName}":`, err)
				return EMPTY
			}),
		)
	}

	find() {
		return zip([
			fromEvent<HERE_RICKY_EVENT>(window, HERE_RICKY).pipe(
				map(e => e.detail),
				bufferTime(0),
			),
			of(1).pipe(tap(() => window.dispatchEvent(this.findingMortiesEvent))),
		]).pipe(
			map(([component]) => component),
			timeout(1),
			catchError(() => EMPTY),
		)
	}

	push(r: RouteAction) {
		if (!r.area) {
			throw new Error('Area is required for route action')
		}

		if (this.isProcessingPopstate) {
			return
		}

		const routeAction: RouteAction = {
			...r,
			state: r.state || {},
			params: r.params || {},
			props: r.props || {},
			_source: 'programmatic' as NavigationSource,
		}

		if (this.enableHistoryMode) {
			routerHistory.next(routeAction)
		}

		this.pendingByArea.set(routeAction.area, routeAction)
		this.request.next(routeAction)
		this.dispatchAreaEvent(routeAction.area, routeAction)
	}

	/** Called only by area components during popstate handling. */
	_updateFromBrowser(routeAction: RouteAction) {
		const enhancedRoute: RouteAction = {
			...routeAction,
			state: routeAction.state || {},
			params: routeAction.params || {},
			props: routeAction.props || {},
			_source: 'browser' as NavigationSource,
		}

		this.isProcessingPopstate = true
		this.request.next(enhancedRoute)
		this.isProcessingPopstate = false
	}

	updateBrowserHistory(
		areaName: string,
		route: ActiveRoute,
		historyStrategy?: string,
		clearQueryParams?: string[] | boolean | null,
		customPath?: string,
	) {
		if (!this.enableHistoryMode) return

		try {
			const currentState = history.state || {}
			const schmancyAreas = currentState.schmancyAreas || {}
			const departed = this.departedQueryKeys(schmancyAreas, areaName, route)

			const areaData: any = {
				component: route.component,
				area: route.area,
			}

			// Persisted so a later reload's constructor-time history.state restore
			// (initializeFromBrowserState) carries the same `when` a live push has —
			// the departure signal's when-to-when comparison needs it on both sides.
			if (route.when) {
				areaData.when = route.when
			}

			if (route.state && Object.keys(route.state).length > 0) {
				areaData.state = route.state
			}

			if (route.params && Object.keys(route.params).length > 0) {
				areaData.params = route.params
			}

			if (route.props && Object.keys(route.props).length > 0) {
				areaData.props = route.props
			}

			// The address this push named for its page, so a Back to this entry tells
			// the same page from a different one by the same fact the push did.
			if (customPath) {
				areaData.path = customPath
			}

			// A silent push records its route on this same entry — later writes from any
			// area carry it forward — but leaves the address as it was, so the names the
			// route it replaces wrote are still standing there. They pass to the new
			// record, which is now the only thing that can take them out.
			if (historyStrategy === 'silent') {
				const address = new URLSearchParams(location.search)
				const held = this.routeQueryKeys(schmancyAreas[areaName]).filter(key => address.has(key))
				if (held.length > 0) areaData.query = held
			}

			schmancyAreas[areaName] = areaData

			const url = this.createCleanURL(schmancyAreas, clearQueryParams, customPath, departed)

			// 'silent' strategy intentionally falls through without touching history.
			if (historyStrategy === 'replace' || historyStrategy === 'pop') {
				// The current entry stays the same entry, so the per-entry keys other owners
				// wrote on it — an overlay's schmancyOverlayId, a host app's own token —
				// survive the rewrite; dropping them would strand each owner on an entry that
				// no longer answers to it.
				history.replaceState({ ...currentState, schmancyAreas }, '', url)
			} else if (historyStrategy === 'push' || !historyStrategy) {
				// A push mints an entry no other owner created, so it starts from a bare bag
				// carrying only what the router owns. Copying a foreign per-entry key forward
				// makes the new entry impersonate the one below it: the overlay's `is my own
				// entry still on top?` guard reads its id back off a route push and hands that
				// entry away with history.back(), undoing the navigation that created it.
				history.pushState({ schmancyAreas }, '', url)
			}
			this._commitAddress()
			this._publishURL()
		} catch (error) {
			console.error('Failed to update browser history:', error)
		}
	}

	/** The query `updateBrowserHistory` would write for this route, written nowhere —
	 *  so an area can ask whether a push would move the address before it commits. */
	_queryFor(
		areaName: string,
		route: ActiveRoute,
		clearQueryParams?: string[] | boolean | null,
		customPath?: string,
	): URLSearchParams {
		const departed = this.departedQueryKeys(history.state?.schmancyAreas, areaName, route)
		const schmancyAreas = { ...history.state?.schmancyAreas, [areaName]: route }
		return new URL(this.createCleanURL(schmancyAreas, clearQueryParams, customPath, departed), location.origin)
			.searchParams
	}

	// The query names a route wrote for itself: its params, and any query its own
	// `path` spelled. They describe a position inside that route's page, so they are
	// the route's to take with it when the area leaves it. Exactly what
	// `createCleanURL` writes, and no more: a null or undefined param is never
	// written. A param the route's pattern declares counts only while the query
	// holds the route's own value for it — written there where the address did not
	// spell it, or by an older address — never a same-named query someone else wrote. `query` holds the names a silent
	// push inherited from the route it replaced.
	private routeQueryKeys(
		route: { when?: string; params?: Record<string, unknown>; path?: unknown; query?: unknown } | undefined,
	): string[] {
		if (!route) return []
		const inPath = new Set(patternParamNames(route.when))
		const address = new URLSearchParams(location.search)
		return [
			...Object.entries(route.params ?? {})
				.filter(
					([key, value]) =>
						value !== null && value !== undefined && (!inPath.has(key) || address.get(key) === String(value)),
				)
				.map(([key]) => key),
			...(typeof route.path === 'string' ? new URL(route.path, location.origin).searchParams.keys() : []),
			...(Array.isArray(route.query) ? route.query.filter(key => typeof key === 'string') : []),
		]
	}

	// The query names an area's recorded route takes with it as `route` replaces it
	// — or as the area goes, when `route` is null. The entry's record is the standing
	// route: every commit writes it, a silent one included (in place, on the current
	// entry's in-memory state, which reaches the saved entry at the next history
	// write), so judging identity against it is judging against the route
	// the area is actually leaving. A re-push of the same route is a move inside its
	// page — a tab, a filter — and keeps the names it does not restate. Identity is
	// `when` to `when` when both sides declare one, tag to tag otherwise, exactly as
	// a departure is judged. A name another recorded area also states stays.
	private departedQueryKeys(
		areas: Record<string, ActiveRoute & { path?: unknown; query?: unknown }> | undefined,
		areaName: string,
		route: ActiveRoute | null,
	): string[] {
		const recorded = areas?.[areaName]
		if (!recorded) return []
		if (route && (recorded.when && route.when ? recorded.when === route.when : recorded.component === route.component))
			return []
		const stillStated = new Set(
			Object.entries(areas)
				.filter(([name]) => name !== areaName)
				.flatMap(([, other]) => this.routeQueryKeys(other)),
		)
		return this.routeQueryKeys(recorded).filter(key => !stillStated.has(key))
	}

	// True when the address in the bar is one this service authored by encoding the
	// areas map into its last segment. That segment is a projection of the map, so it
	// goes stale the moment the map changes and has to be regenerated. An address a
	// `path` push named, one the app or the user typed, is not — regenerating it
	// overwrites a statement this service never made. A pattern-built address is the
	// service's own projection too, tracked separately by `patternAuthoredAddress`
	// because its shape carries no marker to recognise it by.
	private addressEncodesAreas(): boolean {
		const lastSegment = location.pathname.split('/').pop() ?? ''
		return lastSegment.includes('{') || lastSegment.includes('%7B')
	}

	private createCleanURL(
		areas: Record<string, ActiveRoute>,
		clearQueryParams?: string[] | boolean | null,
		customPath?: string,
		departedKeys: string[] = [],
	): string {
		// A dynamic-pattern address-owning-area route (`when="/:eventId"`) writes its
		// captured params into the path itself below, not the query string —
		// collected here so the query-string merge two lines down can skip them. An
		// EMPTY set is not a pattern: a `when` that captures no name has no path to
		// build and falls through to the prettyURL / blob behaviour it has always had.
		const addressAreaRoute = areas[this.addressArea]
		const addressPatternKeys = new Set(patternParamNames(addressAreaRoute?.when))

		// clearQueryParams === true clears every param, the areas' own included. A
		// push naming its `path` asserts the whole address, so its query starts from
		// that path and the routes the areas map states, never from whatever query
		// the previous address happened to carry.
		const urlParams = new URLSearchParams(clearQueryParams === true || customPath ? '' : location.search)

		if (clearQueryParams !== true) {
			// Deleted before the areas write theirs, so a name the new route states
			// itself goes straight back in with the new route's value.
			departedKeys.forEach(key => urlParams.delete(key))

			// A param the route's own pattern declares is placed by `queryFor` below,
			// once the address it has to agree with is known.
			Object.values(areas).forEach(route => {
				const inPath = patternParamNames(route.when)
				Object.entries(route.params ?? {}).forEach(([key, value]) => {
					if (value === null || value === undefined || inPath.includes(key)) return
					urlParams.set(key, String(value))
				})
			})
		}

		// A param a route's pattern declares belongs in the path. Where the address
		// written actually spells it — the encoded segment, or the pattern filled in
		// among its segments — it is not written to the query; a same-named query
		// entry is someone else's and stays. Where it does not (an explicit path, the
		// address area's pattern path, prettyURL: none writes the encoded segment),
		// the query is the only place it can live.
		const queryFor = (pathname: string, encoded: boolean): URLSearchParams => {
			const params = new URLSearchParams(urlParams)
			if (clearQueryParams === true) return params
			const pathSegments = pathname.split('/').filter(Boolean)
			Object.values(areas).forEach(route => {
				if (!isDynamicRoutePattern(route.when)) return
				const built = buildPathFromPattern(route.when, route.params)?.split('/').filter(Boolean)
				const spelled =
					encoded ||
					(built !== undefined &&
						pathSegments.some((_, start) => built.every((segment, index) => pathSegments[start + index] === segment)))
				if (spelled) return
				patternParamNames(route.when).forEach(name => {
					const value = route.params?.[name]
					if (value !== null && value !== undefined) params.set(name, String(value))
				})
			})
			if (Array.isArray(clearQueryParams)) clearQueryParams.forEach(param => params.delete(param))
			return params
		}

		const formatQuery = (params: URLSearchParams) => {
			const query = params.toString()
			return query ? `?${query}` : ''
		}

		// AN EXPLICIT `path` IS THE WHOLE ADDRESS, NOT A PREFIX FOR ONE. A push that
		// names its own path is asserting the URL that destination carries — `/admin`,
		// `/checkout` — so it is written verbatim and neither the encoded-areas segment
		// nor the prettyURL component name below is appended to it: both of those derive
		// an address for a caller who didn't name one, and this caller named one.
		// Appending the blob produced `/admin/%7B%22root%22...%7D` — unreadable,
		// unshareable, and in any app whose FIRST path segment already means something
		// of its own (a tenant handle, a locale) actively wrong, since the blob lands in
		// a segment that app parses itself. That is the shape consumers hand-rolled a
		// `history.replaceState` around rather than use this field — the field failing
		// its one documented job.
		//
		// Dropping the blob loses nothing. `updateBrowserHistory` writes `schmancyAreas`
		// into `history.state` on this same call, and that is what a back/forward and a
		// reload read first. A COLD navigation to the path — pasted, bookmarked, shared,
		// typed — is resolved instead by the area's own startup arm, which matches a path
		// segment against its declared `when` keys. So a destination reached by an
		// explicit path must declare a route whose `when` equals a segment of that path;
		// that declaration is what makes the address round-trip, and without it the area
		// falls to its `.default` exactly as it does for any unrecognised URL.
		if (customPath) {
			// Resolved against the origin so a missing leading slash, a relative segment,
			// and any query or hash the caller wrote into `path` are all read the way a
			// browser reads them. Only pathname/search/hash are taken, so a caller who
			// hands over a whole URL cannot aim replaceState at another origin — which
			// throws a SecurityError and would lose the navigation entirely.
			const custom = new URL(customPath, location.origin)
			// A trailing slash is trimmed so `/admin/` and `/admin` are one address; the
			// root keeps its slash, because `''` leaves replaceState on the current URL
			// rather than moving to `/`.
			const path = custom.pathname.replace(/\/+$/, '') || '/'
			const merged = queryFor(path, false)
			// A param spelled in `path` wins: it is the more specific statement of the
			// address, over a route param of the same name.
			custom.searchParams.forEach((value, key) => merged.set(key, value))
			return `${path}${formatQuery(merged)}${custom.hash}`
		}

		// A dynamic-pattern route on the address-owning area asserts its own address
		// exactly like an explicit `path` push does — the concrete URL is built by
		// substituting its resolved `params` into the pattern, never JSON-blob-
		// encoded, regardless of prettyURL. Falls through to prettyURL/blob-encoding
		// below only when the pattern cannot be built from the params it was given
		// (buildPathFromPattern returns null).
		if (addressPatternKeys.size > 0 && addressAreaRoute?.when) {
			const built = buildPathFromPattern(addressAreaRoute.when, addressAreaRoute.params)
			if (built !== null) {
				return `${built}${formatQuery(queryFor(built, false))}`
			}
		}

		// No `path` given: the address is derived from where the browser already is.
		// Everything except a last segment that is itself encoded state is the base.
		const currentPath = location.pathname
		const pathSegments = currentPath.split('/')

		let basePath: string
		if (this.addressEncodesAreas()) {
			pathSegments.pop()
			basePath = pathSegments.join('/') || '/'
		} else if (this.patternAuthoredAddress === currentPath) {
			// A PATTERN-BUILT ADDRESS IS THIS SERVICE'S OWN PROJECTION, NOT A BASE TO
			// NEST UNDER. It is exactly as disposable as the encoded blob above, and
			// `buildPathFromPattern` writes it from the root, so what it leaves behind
			// when the address area moves to a non-pattern route is the root. Reading
			// it as a prefix instead produced `/evt123/%7B…%7D` — and since that
			// address is not a blob either, the segment after it nested one level
			// deeper again on every following navigation.
			basePath = '/'
		} else {
			basePath = currentPath
		}

		if (basePath !== '/' && !basePath.endsWith('/')) {
			basePath += '/'
		}

		if (this.prettyURL) {
			if (addressAreaRoute) {
				let path = basePath === '/' ? `/${addressAreaRoute.component}` : `${basePath}${addressAreaRoute.component}`

				const searchParams = queryFor(path, false)
				if (addressAreaRoute.params) {
					Object.entries(addressAreaRoute.params).forEach(([key, value]) => {
						if (typeof value === 'string' || typeof value === 'number') {
							searchParams.set(key, String(value))
						}
					})
				}

				const query = searchParams.toString()
				return path + (query ? `?${query}` : '')
			}
		}

		// prettyURL off, or no main area to build a pretty path from — every area's
		// route is JSON-encoded into the URL instead, empty state/params/props
		// omitted so the encoded segment doesn't balloon with `{}` noise.
		try {
			type CleanRoute = Omit<ActiveRoute, 'area'>
			const cleanedAreas: Record<string, CleanRoute> = {}
			Object.entries(areas).forEach(([areaName, route]) => {
				// An exact `when` beside the tag, so a link outliving a rename resolves by
				// the route's name — or the alias the renamed route keeps — not by a tag.
				// A pattern is left out: its address never carries a literal `:param`.
				const cleanRoute: CleanRoute =
					route.when && !isDynamicRoutePattern(route.when)
						? { component: route.component, when: route.when }
						: { component: route.component }

				if (route.state && Object.keys(route.state).length > 0) {
					cleanRoute.state = route.state
				}

				if (route.params && Object.keys(route.params).length > 0) {
					cleanRoute.params = route.params
				}

				if (route.props && Object.keys(route.props).length > 0) {
					cleanRoute.props = route.props
				}

				cleanedAreas[areaName] = cleanRoute
			})

			const cleanBasePath = basePath === '/' ? '' : basePath.replace(/\/$/, '')
			if (Object.keys(cleanedAreas).length === 0) {
				return `${cleanBasePath}${formatQuery(queryFor(cleanBasePath, false))}`
			}

			const encoded = encodeURIComponent(JSON.stringify(cleanedAreas))
			return `${cleanBasePath}/${encoded}${formatQuery(queryFor(cleanBasePath, true))}`
		} catch (error) {
			console.error('Failed to encode URL state:', error)
			return location.pathname
		}
	}

	restoreFromBrowserState(browserState: any): Record<string, ActiveRoute> {
		try {
			if (browserState && browserState.schmancyAreas) {
				return browserState.schmancyAreas
			}
		} catch (error) {
			console.error('Failed to restore from browser state:', error)
		}

		return this.parseStateFromURL()
	}

	private parseStateFromURL(): Record<string, ActiveRoute> {
		const segment = location.pathname.split('/').pop()
		if (!segment) return {}

		try {
			const parsed: unknown = JSON.parse(decodeURIComponent(segment))
			if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}

			// A path segment is arbitrary input. Only an entry naming a component is an area;
			// anything else would be seeded into the area map and re-encoded into every url
			// this service writes from here on.
			const areas: Record<string, ActiveRoute> = {}
			Object.entries(parsed).forEach(([areaName, route]) => {
				if (route && typeof route === 'object' && typeof route.component === 'string') areas[areaName] = route
			})
			return areas
		} catch {
			// Not an encoded-state segment at all — a plain path, or malformed percent-encoding.
			return {}
		}
	}

	private dispatchAreaEvent(areaName: string, routeAction: RouteAction) {
		const eventName = `schmancy-area-${areaName}-changed`
		const event = new CustomEvent(eventName, {
			detail: {
				area: areaName,
				component: routeAction.component,
				state: routeAction.state,
				params: routeAction.params,
				props: routeAction.props,
				historyStrategy: routeAction.historyStrategy,
			},
			bubbles: true,
			composed: true,
		})
		window.dispatchEvent(event)
	}

	_publishURL() {
		if (typeof location === 'undefined') return
		this.urlSubject.next(location.href)
	}

	_commitAddress() {
		if (typeof location === 'undefined') return
		this.committedAddress = `${location.pathname}${location.search}`
		const addressRoute = this.current.get(this.addressArea)
		this.patternAuthoredAddress =
			addressRoute?.when &&
			isDynamicRoutePattern(addressRoute.when) &&
			buildPathFromPattern(addressRoute.when, addressRoute.params) === location.pathname
				? location.pathname
				: null
	}

	_popstateMatchesCommittedDocument(event: PopStateEvent): boolean {
		if (typeof location === 'undefined') return false

		const remembered = this.popstateDocumentMatches.get(event)
		if (remembered !== undefined) return remembered

		const matches = this.committedAddress === `${location.pathname}${location.search}`
		this.popstateDocumentMatches.set(event, matches)
		return matches
	}

	pop(name: string) {
		if (!name) {
			throw new Error('Area name is required')
		}

		// Emit a clearing signal (null component) so the area component clears itself
		// before this leaves the current map.
		const areaSubject = this.areaSubjects.get(name)
		if (areaSubject && !areaSubject.closed) {
			areaSubject.next({
				component: null as any,
				state: {},
				area: name,
				params: {},
				props: {},
			})
		}

		this.request.next({
			area: name,
			component: null as any,
			state: {},
			params: {},
			props: {},
			historyStrategy: 'silent' as any,
			_source: 'programmatic' as NavigationSource,
		})

		this.current.delete(name)
		this.$current.next(this.current)

		if (this.enableHistoryMode) {
			try {
				const currentState = history.state || {}
				const schmancyAreas = { ...currentState.schmancyAreas }
				const departed = this.departedQueryKeys(schmancyAreas, name, null)
				delete schmancyAreas[name]

				const newState = {
					...currentState,
					schmancyAreas,
				}

				// POPPING AN AREA IS NOT A STATEMENT ABOUT THE WHOLE ADDRESS. The entry
				// still has to record that this area is gone, so the state is rewritten
				// either way — but the URL is regenerated only when the URL is itself a
				// projection of the areas map, which is exactly the encoded-segment case.
				// Regenerating any other address overwrites one this service never wrote:
				// under an explicit `path` it replaced `/sign-in` with
				// `/sign-in/%7B...%7D` the moment a nested area disconnected, stranding
				// the app on an address its own routing no longer recognised; under
				// prettyURL it appended the main component's name to the current path on
				// every pop, growing the address a segment at a time. The one part of
				// any address the popped area did write is its route's own query, and
				// that leaves with the route.
				const query = new URLSearchParams(location.search)
				departed.forEach(key => query.delete(key))
				const search = query.toString()
				const url = this.addressEncodesAreas()
					? this.createCleanURL(schmancyAreas, undefined, undefined, departed)
					: `${location.pathname}${search ? `?${search}` : ''}${location.hash}`
				history.replaceState(newState, '', url)
				this._commitAddress()
				this._publishURL()
			} catch (error) {
				console.error('Failed to update history after pop:', error)
			}
		}
	}

	/** Claim and remove the pending startup route for an area — returns undefined if none.
	 * Note: an entry pushed to a name that never mounts persists until the next push to that name;
	 * for string/lazy `component` values this is negligible, but an HTMLElement instance would be
	 * retained — push instances only to areas you know will mount. */
	consumePending(areaName: string): RouteAction | undefined {
		const p = this.pendingByArea.get(areaName)
		this.pendingByArea.delete(areaName)
		return p
	}

	clear() {
		this.areaSubjects.forEach(subject => subject.complete())
		this.areaSubjects.clear()

		this.current.clear()
		this.$current.next(this.current)

		if (this.enableHistoryMode) {
			const url = this.createCleanURL({})
			history.replaceState({ schmancyAreas: {} }, '', url)
			this._commitAddress()
			this._publishURL()
		}
	}

	dispose() {
		if (this.disposed) return

		this.disposed = true

		if (this.browserSubscription) {
			this.browserSubscription.unsubscribe()
			this.browserSubscription = undefined
		}

		this.areaSubjects.forEach(subject => subject.complete())
		this.areaSubjects.clear()

		this.request.complete()
		this.$current.complete()
		this.urlSubject.complete()
		this.departureSubject.complete()
		routerHistory.complete()

		this.current.clear()
		areaSubjectsCache.delete(this)
	}

	static getInstance() {
		if (!AreaService.instance) {
			AreaService.instance = new AreaService()
		}
		return AreaService.instance
	}

	/** @deprecated Use browser state instead. */
	get state(): Record<string, unknown> {
		try {
			const browserState = history.state
			if (browserState && browserState.schmancyAreas) {
				return browserState.schmancyAreas
			}
		} catch {
			// Fallback to URL parsing.
		}

		return this.parseStateFromURL()
	}

	hasArea(areaName: string): boolean {
		return this.current.has(areaName)
	}

	getActiveAreas(): string[] {
		return Array.from(this.current.keys())
	}

	getRoute(areaName: string): ActiveRoute | undefined {
		return this.current.get(areaName)
	}
}

// Area router singleton. Drives named `<schmancy-area>` outlets in the DOM
// — imperative push / pop, URL-synced, state-restoring. Pair with
// `lazy(() => import('./view'))` for code-split routes.
export const area = AreaService.getInstance()
export default area
